import React, { useState, useEffect, useCallback } from 'react';

/**
 * Dashboard — User Ledger (Ledger view).
 *
 * Each settled bet renders in the same two-row layout as SettledBox:
 *   Row 1 — OPEN / CLOSE  (+delta)              WIN/LOSS  +net
 *   Row 2 — [slot time]  ratio  payout  amount
 *
 * Shows last 30 days of history fetched from /api/ledger/history.
 * Older data available via CSV download (/api/ledger/archive).
 */

const COIN_LABEL = {
    bitcoin:  'BTC',
    ethereum: 'ETH',
    solana:   'SOL',
    ripple:   'XRP',
    dogecoin: 'DOGE',
};

/* ── helpers ── */
const fmtTime = (iso) => {
    const d = new Date(iso);
    const h = d.getHours() % 12 || 12;
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
};

const fmtDate = (iso) => {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};

const fmtPrice = (v) =>
    parseFloat(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

const fmtCredits = (sol) => Math.round(parseFloat(sol || 0) * 100);

/* ── Summary card ── */
const SummaryCard = ({ label, value, color }) => (
    <div className="bg-gradient-to-br from-gray-900 to-black border border-white/5 p-5 rounded-sm shadow-xl relative overflow-hidden group">
        <div className={`absolute top-0 right-0 w-28 h-28 blur-3xl -mr-12 -mt-12 rounded-full transition-colors bg-${color}-500/5 group-hover:bg-${color}-500/10`} />
        <div className={`text-[9px] uppercase tracking-[0.3em] mb-3 font-bold text-${color}-500/50`}>{label}</div>
        <div className={`text-2xl font-mono text-${color}-400 tracking-tighter`}>{value}</div>
    </div>
);

/* ── Single settled row pair (mirrors SettledBox layout exactly) ── */
const SettledRow = ({ row }) => {
    const openP  = parseFloat(row.open_price  || 0);
    const closeP = parseFloat(row.close_price || 0);
    const diff   = closeP - openP;
    const isUp   = diff > 0;
    const isPush = diff === 0;

    const amountCr = fmtCredits(row.amount_sol);
    const isWin    = row.result === 'WIN';
    const netCr    = isWin
        ? Math.round(parseFloat(row.payout_sol) * 100 - parseFloat(row.amount_sol) * 100)
        : -amountCr;

    const dirUp = row.direction === 'UP';
    const winColour = isWin ? 'text-green-400' : 'text-red-400';
    const rowBg = dirUp ? 'bg-green-500/10' : 'bg-red-500/10';

    return (
        <>
            {/* ── Row 1: price + result ── */}
            <div className={`relative flex items-center px-3 h-[25px] border-b border-white/5 ${rowBg}`}>
                {/* Open / Close */}
                <span className="font-mono text-sm font-normal text-blue-400 tracking-tight">
                    {fmtPrice(row.open_price)}
                </span>
                <span className="font-mono text-sm font-normal text-white/40 mx-1">/</span>
                <span className="font-mono text-sm font-normal text-white tracking-tight">
                    {fmtPrice(row.close_price)}
                </span>
                {/* Delta */}
                <span className={`font-mono text-xs font-normal ml-2 ${
                    isUp ? 'text-green-400' : isPush ? 'text-white/40' : 'text-red-400'
                }`}>
                    ({diff >= 0 ? '+' : ''}{diff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </span>
                {/* WIN/LOSS badge + net */}
                <span className={`ml-auto mr-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    isPush ? 'bg-white/10 text-white/60' :
                    isWin  ? 'bg-green-500/20 text-green-400' :
                             'bg-red-500/20 text-red-400'
                }`}>
                    {isPush ? 'PUSH' : isWin ? 'WIN' : 'LOSS'}
                </span>
                <span className={`font-mono text-sm font-bold tracking-tight ${winColour}`}>
                    {isWin ? `+${netCr}` : `${netCr}`}
                </span>
            </div>

            {/* ── Row 2: bet detail ── */}
            <div className={`flex items-center h-[33px] border-b border-gray-800/30 ${rowBg}`}>
                {/* Time chip */}
                <div className="flex items-center pl-2 min-w-[5rem] w-[5rem]">
                    <span className={`font-mono rounded px-2 py-0.5 text-xs border min-w-[3.5rem] text-center ${
                        dirUp
                            ? 'text-green-400 border-green-500 bg-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.3)]'
                            : 'text-red-400 border-red-500 bg-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.3)]'
                    }`}>
                        {fmtTime(row.slot_time)}
                    </span>
                </div>

                {/* Coin + direction */}
                <div className="flex items-center gap-2 pl-2 min-w-[70px]">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        {COIN_LABEL[row.coin] || row.coin?.toUpperCase()}
                    </span>
                    <span className={`text-[9px] font-black tracking-widest ${dirUp ? 'text-green-400' : 'text-red-400'}`}>
                        {dirUp ? '▲' : '▼'}
                    </span>
                </div>

                {/* Amount */}
                <div className="flex flex-col items-center justify-center min-w-[75px] border-l border-white/5 h-full">
                    <span className={`text-sm font-mono font-normal leading-none tracking-tighter ${dirUp ? 'text-green-400' : 'text-red-400'}`}>
                        {amountCr.toLocaleString()}
                    </span>
                </div>

                {/* Date — right side */}
                <div className="ml-auto pr-3 text-[9px] text-gray-600 font-mono">
                    {fmtDate(row.settled_at)}
                </div>
            </div>
        </>
    );
};

/* ── Main component ── */
const Dashboard = ({ balance }) => {
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);
    const [archiving, setArchiving] = useState(false);

    const loadHistory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await fetch('/api/ledger/history?days=30', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load');
            const data = await r.json();
            setRows(data.rows || []);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const handleArchive = async () => {
        setArchiving(true);
        try {
            const r = await fetch('/api/ledger/archive', { credentials: 'include' });
            if (!r.ok) throw new Error('Archive failed');
            const blob = await r.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `minutemarket_ledger_${new Date().toISOString().slice(0,10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { /* silent */ }
        setArchiving(false);
    };

    /* ── Summary numbers (30-day window) ── */
    const totalWins   = rows.filter(r => r.result === 'WIN').reduce((s, r) =>
        s + (parseFloat(r.payout_sol) * 100 - parseFloat(r.amount_sol) * 100), 0);
    const totalLosses = rows.filter(r => r.result === 'LOSS').reduce((s, r) =>
        s + parseFloat(r.amount_sol) * 100, 0);
    const totalFees   = rows.reduce((s, r) => s + parseFloat(r.house_fee_sol || 0) * 100, 0);

    return (
        <div className="p-6 max-w-4xl mx-auto text-gray-300">
            {/* Header */}
            <div className="flex justify-between items-baseline mb-8 border-b border-gray-800 pb-5">
                <h1 className="text-3xl font-light tracking-[0.2em] uppercase text-white">
                    Ledger
                </h1>
                <div className="flex items-center gap-4">
                    <span className="text-gray-600 font-mono text-[10px] uppercase tracking-widest">
                        Last 30 days
                    </span>
                    <button
                        onClick={handleArchive}
                        disabled={archiving}
                        id="ledger-archive-btn"
                        className="flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded bg-white/[0.03] text-[10px] text-gray-400 uppercase tracking-widest font-bold hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition-all disabled:opacity-40"
                    >
                        {archiving ? '⏳' : '⬇'} Archive CSV
                    </button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <SummaryCard
                    label="Credits Balance"
                    value={(balance * 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    color="blue"
                />
                <SummaryCard
                    label="30d Wins"
                    value={`+${totalWins.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    color="green"
                />
                <SummaryCard
                    label="30d Losses"
                    value={`-${totalLosses.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    color="red"
                />
                <SummaryCard
                    label="30d Fees"
                    value={totalFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    color="amber"
                />
            </div>

            {/* Transaction list */}
            <div className="bg-black/40 border border-white/5 rounded-sm overflow-hidden">
                {/* Column header */}
                <div className="grid grid-cols-[5rem_1fr_80px_80px_1fr] px-3 py-2 border-b border-white/5 bg-gray-900/60 text-[9px] uppercase tracking-[0.25em] text-gray-500 font-black">
                    <span>Time</span>
                    <span>Prices</span>
                    <span className="text-right">Amount</span>
                    <span className="text-center">Result</span>
                    <span className="text-right pr-3">Net</span>
                </div>

                <div className="overflow-y-auto max-h-[65vh] custom-scrollbar">
                    {loading ? (
                        <div className="py-20 text-center text-gray-600 font-mono text-xs uppercase tracking-widest">
                            Loading…
                        </div>
                    ) : error ? (
                        <div className="py-20 text-center text-red-500 font-mono text-xs">
                            ❌ {error}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="py-20 text-center text-gray-700 font-mono text-xs uppercase tracking-widest">
                            No settled bets in the last 30 days
                        </div>
                    ) : (
                        rows.map((row, i) => (
                            <SettledRow key={`${row.bet_id}_${i}`} row={row} />
                        ))
                    )}
                </div>

                {/* Footer */}
                {rows.length > 0 && !loading && (
                    <div className="px-3 py-2 border-t border-white/5 bg-gray-900/40 flex items-center justify-between">
                        <span className="text-[9px] text-gray-600 uppercase tracking-widest font-bold">
                            {rows.length} settlement{rows.length !== 1 ? 's' : ''} · last 30 days
                        </span>
                        <button
                            onClick={handleArchive}
                            disabled={archiving}
                            className="text-[9px] text-gray-500 hover:text-cyan-400 uppercase tracking-widest font-bold transition-colors disabled:opacity-40"
                        >
                            {archiving ? 'Preparing…' : '⬇ Download All History (CSV)'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
